import { faroCoreApiClient } from "@api/faro-core-api-client";
import { AdmDataGrid } from "@components/common/data-grid/adm-data-grid";
import { ExportMenuButton } from "@components/common/data-grid/toolbar/export-menu-button";
import { FilterDropdown } from "@components/common/data-grid/toolbar/filter-dropdown";
import { getFilterSelection } from "@components/common/data-grid/toolbar/toolbar-helper";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { Box, Stack } from "@mui-wrapper";
import { GridColumnGroupingModel } from "@mui/x-data-grid-pro";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getBundleFilterOptions,
  getSubscriptionFilterOptions,
  getSubscriptions,
} from "@pages/about-products/about-products-filter-options";
import { useAboutProductsFilters } from "@pages/about-products/use-about-products-filters";
import {
  getAboutProductsExportFileName,
  getAboutProductsExportRows,
} from "@pages/about-products/about-products-export";
import { BundleFeatureChange } from "@pages/about-products/about-products-table-types";
import { BundleFeatureChangeDialog } from "@pages/about-products/bundle-feature-change-dialog";
import { useAboutProductsTableColumns } from "@pages/about-products/use-about-products-table-columns";
import { useAboutProductsTableRows } from "@pages/about-products/use-about-products-table-rows";
import { Spinner } from "@components/common/spinner";
import { CreateBundleDialog } from "@components/create-bundle-dialog";
import { CreateFeatureDialog } from "@components/create-feature-dialog";
import { appStateSelector } from "@store/app/app-slice";
import { useAppSelector } from "@store/store-helper";
import { Button, Text } from "@flat-ui";
import { ExportFormat, exportRows } from "@utils/file-export";
import {
  ExportTableFilterProps,
  TableType,
} from "@utils/track-event/event-types";
import { GeneralTableEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

const TABLE_TYPE: TableType = "about-products-table";

/** Shows about product page illustrating which features are available in each bundle or custom product */
export function AboutProductsPage(): React.JSX.Element {
  const { isDevModeEnabled } = useAppSelector(appStateSelector);
  const { handleErrorWithToast } = useErrorHandler();
  const [allFeatures, setAllFeatures] = useState<FaroCoreApiTypes.AdmFeature[]>(
    []
  );
  const [allBundles, setAllBundles] = useState<FaroCoreApiTypes.AdmBundle[]>(
    []
  );
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [pendingChange, setPendingChange] =
    useState<BundleFeatureChange | null>(null);
  const [selection, updateSelection] = useAboutProductsFilters();
  const { trackEvent } = useTrackEvent();

  const fetchBundles = useCallback(async (): Promise<void> => {
    setAllBundles(await faroCoreApiClient.admin.getAllBundles());
  }, []);

  // Fetch all features and bundles on component mount
  useEffect(() => {
    async function fetchFeaturesAndBundles(): Promise<void> {
      setIsFetching(true);
      try {
        setAllFeatures(await faroCoreApiClient.admin.getAllFeatures());
        await fetchBundles();
      } catch {
        throw new Error("Could not fetch Features or Bundles");
      } finally {
        setIsFetching(false);
      }
    }

    void fetchFeaturesAndBundles();
  }, [fetchBundles]);

  function handleChangeApplied(): void {
    setPendingChange(null);

    fetchBundles().catch((error: unknown) => {
      handleErrorWithToast({
        id: `refresh-bundles-${Date.now().toString()}`,
        title: "Could not refresh the bundles",
        error,
      });
    });
  }

  const bundleOptions = useMemo(
    () => getBundleFilterOptions(allBundles),
    [allBundles]
  );

  /** Every subscription that can be compared, in the order of the Add Subscription page */
  const subscriptions = useMemo(
    () => getSubscriptions(allBundles),
    [allBundles]
  );

  const subscriptionOptions = useMemo(
    () => getSubscriptionFilterOptions(subscriptions),
    [subscriptions]
  );

  const visibleSubscriptions = useMemo(
    () =>
      subscriptions.filter(({ id }) => selection.subscriptionIds.includes(id)),
    [subscriptions, selection.subscriptionIds]
  );

  const visibleBundles = useMemo(
    () =>
      allBundles.filter((bundle) =>
        selection.bundleIdentifiers.includes(bundle.identifier)
      ),
    [allBundles, selection.bundleIdentifiers]
  );

  const hasActiveFilters =
    selection.subscriptionIds.length > 0 ||
    selection.bundleIdentifiers.length > 0;

  /** Tracks the option that was just added, like the shared filter toolbar does */
  function trackAddedOption(previousIds: string[], nextIds: string[]): void {
    const addedIds = nextIds.filter((id) => !previousIds.includes(id));
    const [addedId] = addedIds;

    if (!addedId) {
      return;
    }

    trackEvent({
      name: GeneralTableEvents.applyFilter,
      props: {
        location: TABLE_TYPE,
        // No single id describes a bulk selection, which adds every option at once
        filterType: addedIds.length > 1 ? "select all" : addedId,
      },
    });
  }

  /** Removes the selection of both dropdowns */
  function clearAllFilters(): void {
    trackEvent({
      name: GeneralTableEvents.clearFilters,
      props: {
        location: TABLE_TYPE,
      },
    });

    updateSelection({ subscriptionIds: [], bundleIdentifiers: [] });
  }

  const columnGroupingModel = useMemo<GridColumnGroupingModel>(() => {
    const groupingModel: GridColumnGroupingModel = [
      {
        groupId: "features",
        headerName: "Features",
        children: [{ field: "featureName" }, { field: "featureIdentity" }],
      },
    ];

    if (visibleSubscriptions.length > 0) {
      groupingModel.push({
        groupId: "subscriptions",
        headerName: "Subscriptions",
        children: visibleSubscriptions.map(({ id }) => ({ field: id })),
      });
    }

    if (visibleBundles.length > 0) {
      groupingModel.push({
        groupId: "bundles",
        headerName: "Bundles",
        children: visibleBundles.map((bundle) => ({ field: bundle.id })),
      });
    }

    return groupingModel;
  }, [visibleSubscriptions, visibleBundles]);

  const columns = useAboutProductsTableColumns(
    visibleBundles,
    visibleSubscriptions,
    isDevModeEnabled ? setPendingChange : undefined
  );

  const rows = useAboutProductsTableRows(
    allFeatures,
    allBundles,
    visibleBundles,
    visibleSubscriptions
  );

  /** How much of each filter is selected, tracked when the table is exported */
  const exportFilterProps: ExportTableFilterProps = {
    subscriptions: getFilterSelection(
      visibleSubscriptions.length,
      subscriptions.length
    ),
    bundles: getFilterSelection(visibleBundles.length, allBundles.length),
  };

  /** Downloads the features with the subscriptions and bundles selected in the filters */
  function exportProducts(format: ExportFormat): void {
    exportRows({
      rows: getAboutProductsExportRows(
        rows,
        visibleSubscriptions,
        visibleBundles
      ),
      fileName: getAboutProductsExportFileName(),
      format,
    }).catch((error: unknown) => {
      handleErrorWithToast({
        id: `export-products-${Date.now().toString()}`,
        title: "Could not export the product list",
        error,
      });
    });
  }

  return (
    <Box
      sx={{
        mx: 2.5,
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 160px)",
      }}
    >
      <Text variant="heading20">About products</Text>
      <Text variant="bodyM">
        This table lists every feature the backend knows about. Use the filters
        to choose which subscriptions and bundles to compare them against.
      </Text>

      {isFetching ? (
        <Spinner />
      ) : (
        <>
          <Stack
            sx={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "flex-start",
              gap: 1,
              my: 2,
            }}
          >
            <FilterDropdown
              label="Subscriptions"
              options={subscriptionOptions}
              selectedIds={selection.subscriptionIds}
              hasSelectAll
              onChange={(subscriptionIds) => {
                trackAddedOption(selection.subscriptionIds, subscriptionIds);
                updateSelection({ subscriptionIds });
              }}
            />
            <FilterDropdown
              label="Bundles"
              options={bundleOptions}
              selectedIds={selection.bundleIdentifiers}
              hasSelectAll
              onChange={(bundleIdentifiers) => {
                trackAddedOption(
                  selection.bundleIdentifiers,
                  bundleIdentifiers
                );
                updateSelection({ bundleIdentifiers });
              }}
            />

            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearAllFilters}>
                Clear All Filters
              </Button>
            )}
          </Stack>

          <Box
            sx={{
              height: "75vh",
            }}
          >
            <AdmDataGrid
              entities={rows}
              uniqueIdKey="id"
              tableType={TABLE_TYPE}
              columns={columns}
              defaultSortModel={[{ field: "featureName", sort: "asc" }]}
              shouldShowErrorMessage={false}
              errorMessage=""
              shouldShowVerticalGridLines={true}
              shouldHighlightHoveredColumn={true}
              shouldAllowColumnResize={true}
              pinnedLeftColumns={["featureName", "featureIdentity"]}
              columnGroupingModel={columnGroupingModel}
              leadingToolbarButtons={
                rows.length > 0 && (
                  <ExportMenuButton
                    tableType={TABLE_TYPE}
                    filterProps={exportFilterProps}
                    onExport={exportProducts}
                  />
                )
              }
              additionalToolbarButtons={
                isDevModeEnabled && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <CreateFeatureDialog />
                    <CreateBundleDialog />
                  </Box>
                )
              }
            />
          </Box>
        </>
      )}

      {pendingChange ? (
        <BundleFeatureChangeDialog
          change={pendingChange}
          onApplied={handleChangeApplied}
          onClose={() => setPendingChange(null)}
        />
      ) : null}
    </Box>
  );
}
